import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Wallet, Gift, Check, LogOut, ArrowRight, Clock } from 'lucide-react';
import { API, trackEvent } from './peptideContent';

const H = { fontFamily: 'Bebas Neue, sans-serif' };
const TOKEN_KEY = 'bhn_member_token';

const RULE_LABELS = {
  renewal_network: 'Membership renewal — Network', renewal_pro: 'Membership renewal — Pro',
  renewal_elite: 'Membership renewal — Elite', referral_approved: 'Referral approved',
  education_module: 'Education module completed', webinar_attendance: 'Webinar attendance',
  profile_completion: 'Profile completed', demo_grant: 'Bonus points',
};

const txnLabel = (t) => {
  if ((t.description || '').startsWith('merch order')) return 'Merch redemption';
  return RULE_LABELS[t.description] || t.description || t.type;
};

const EmailGate = ({ onUnlock, error }) => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onUnlock(email.trim());
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="border border-zinc-800 bg-zinc-900/40 p-8 max-w-md" data-testid="wallet-email-gate">
      <p className="text-zinc-400 text-sm leading-relaxed mb-5">
        Enter the email you used for your Biohacker Network membership to open your points wallet.
        Your email is never stored — only a one-way hash identifies your account.
      </p>
      <input
        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com" aria-label="Membership email"
        className="w-full bg-zinc-900 border border-zinc-700 text-white text-sm px-3 py-3 focus:border-[#C7FA3E] outline-none mb-3"
        data-testid="wallet-email-input"
      />
      {error && <p className="text-red-400 text-xs mb-3" data-testid="wallet-gate-error">{error}</p>}
      <button type="submit" disabled={busy || !email.trim()}
        className="w-full bg-[#C7FA3E] text-black text-sm font-bold uppercase tracking-wider py-3 disabled:opacity-40 hover:bg-[#d4ff5c] transition-colors"
        data-testid="wallet-unlock-btn">
        {busy ? 'Opening…' : 'Open My Wallet'}
      </button>
    </form>
  );
};

const GiftBanner = ({ gift, onClaim, claiming }) => {
  if (!gift || gift.status === 'none') return null;
  const base = 'border p-5 mb-8 flex flex-wrap items-center gap-4';
  if (gift.status === 'available') {
    return (
      <div className={`${base} border-[#C7FA3E]/40 bg-[#C7FA3E]/[0.05]`} data-testid="wallet-gift-banner">
        <Gift className="w-5 h-5 text-[#C7FA3E]" />
        <p className="text-zinc-300 text-sm flex-1 min-w-[200px]">Your complimentary welcome gift is ready to claim.</p>
        <button onClick={onClaim} disabled={claiming}
          className="bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-wider px-5 py-2.5 disabled:opacity-40 hover:bg-[#d4ff5c] transition-colors inline-flex items-center gap-2"
          data-testid="wallet-gift-claim-btn">
          {claiming ? 'Opening…' : 'Claim Gift'} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  if (gift.status === 'pending') {
    return (
      <div className={`${base} border-zinc-800 bg-zinc-900/40`} data-testid="wallet-gift-banner">
        <Clock className="w-5 h-5 text-zinc-500" />
        <p className="text-zinc-400 text-sm">Your welcome gift unlocks after the refund period — available {String(gift.available_at || '').slice(0, 10)}.</p>
      </div>
    );
  }
  if (gift.status === 'redeemed') {
    return (
      <div className={`${base} border-zinc-800 bg-zinc-900/40`} data-testid="wallet-gift-banner">
        <Check className="w-5 h-5 text-[#C7FA3E]" />
        <p className="text-zinc-400 text-sm">Welcome gift redeemed. Nice pick.</p>
      </div>
    );
  }
  return null;
};

export default function BiohackerWalletPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [wallet, setWallet] = useState(null);
  const [gateError, setGateError] = useState('');
  const [redeeming, setRedeeming] = useState(null);
  const [redeemed, setRedeemed] = useState(null);
  const [redeemError, setRedeemError] = useState('');
  const [claiming, setClaiming] = useState(false);

  useEffect(() => { document.title = 'Network Points Wallet | NinjAthlete'; trackEvent('NA_wallet_view'); }, []);

  const load = useCallback(async (t) => {
    try {
      const r = await fetch(`${API}/api/referral/wallet?member_token=${encodeURIComponent(t)}`);
      if (r.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setGateError('Your wallet session expired — enter your email again.');
        return;
      }
      if (!r.ok) throw new Error((await r.json()).detail || 'Could not load wallet');
      setWallet(await r.json());
    } catch (e) { setGateError(e.message); }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const unlock = async (email) => {
    setGateError('');
    try {
      const r = await fetch(`${API}/api/bhn/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Could not open wallet');
      const d = await r.json();
      localStorage.setItem(TOKEN_KEY, d.member_token);
      setToken(d.member_token);
    } catch (e) { setGateError(e.message); }
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setWallet(null);
    setRedeemed(null);
    setGateError('');
  };

  const redeem = async (item) => {
    setRedeeming(item.catalog_item_id);
    setRedeemError('');
    trackEvent('NA_points_redemption_started');
    try {
      const r = await fetch(`${API}/api/referral/points/redeem`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ member_token: token, catalog_item_id: item.catalog_item_id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Redemption failed');
      trackEvent('NA_points_redeemed');
      setRedeemed({ order_id: d.order_id, title: item.title });
      await load(token);
    } catch (e) { setRedeemError(e.message); } finally { setRedeeming(null); }
  };

  const claimGift = async () => {
    setClaiming(true);
    try {
      const r = await fetch(`${API}/api/referral/gift/link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ member_token: token }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not open gift');
      navigate(d.redemption_url);
    } catch (e) { setRedeemError(e.message); setClaiming(false); }
  };

  const available = wallet?.balance?.available ?? 0;

  return (
    <div className="bg-[#0a0a0a]" data-testid="wallet-page">
      <div className="max-w-4xl mx-auto px-5 pt-28 pb-24">
        <p className="text-[#C7FA3E] text-xs uppercase tracking-[3px] mb-3 flex items-center gap-2"><Wallet className="w-4 h-4" /> Biohacker Network Rewards</p>
        <h1 className="text-white text-4xl sm:text-5xl mb-3" style={H}>Network Points Wallet</h1>
        <p className="text-zinc-500 text-sm mb-10 max-w-2xl">
          Points apply to eligible NinjAthlete merchandise only — never to medical services or prescriptions.
          100 points = $1 in merch value · 1,000-point minimum redemption.
        </p>

        {!token ? (
          <EmailGate onUnlock={unlock} error={gateError} />
        ) : !wallet ? (
          gateError ? <EmailGate onUnlock={unlock} error={gateError} /> : <p className="text-zinc-500 text-sm">Loading your wallet…</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div className="border border-[#C7FA3E]/40 bg-[#C7FA3E]/[0.04] p-6" data-testid="wallet-balance-card">
                <p className="text-zinc-500 text-[10px] uppercase tracking-[2px] mb-1">Available points</p>
                <p className="text-white text-4xl font-bold" data-testid="wallet-available-points">{available.toLocaleString()}</p>
                <p className="text-[#C7FA3E] text-xs mt-1">≈ ${(available / 100).toFixed(2)} in merch value</p>
              </div>
              <div className="border border-zinc-800 bg-zinc-900/40 p-6" data-testid="wallet-pending-card">
                <p className="text-zinc-500 text-[10px] uppercase tracking-[2px] mb-1">Pending points</p>
                <p className="text-white text-4xl font-bold">{(wallet.balance.pending || 0).toLocaleString()}</p>
                <p className="text-zinc-500 text-xs mt-1">Unlock after their waiting period</p>
              </div>
              <div className="border border-zinc-800 bg-zinc-900/40 p-6 flex flex-col justify-between">
                <p className="text-zinc-500 text-xs leading-relaxed">Points expire after 12 months of inactivity and have no cash value.</p>
                <button onClick={signOut} className="text-zinc-500 hover:text-white text-xs uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors mt-3" data-testid="wallet-signout-btn">
                  <LogOut className="w-3.5 h-3.5" /> Sign out of wallet
                </button>
              </div>
            </div>

            <GiftBanner gift={wallet.gift} onClaim={claimGift} claiming={claiming} />

            {redeemed && (
              <div className="border border-[#C7FA3E]/40 bg-[#C7FA3E]/[0.05] p-5 mb-8 flex items-center gap-3" data-testid="wallet-redeem-success">
                <Check className="w-5 h-5 text-[#C7FA3E]" />
                <p className="text-zinc-300 text-sm">Redeemed <strong className="text-white">{redeemed.title}</strong> — order <strong className="text-white">{redeemed.order_id}</strong> confirmed for $0.00.</p>
              </div>
            )}
            {redeemError && <p className="text-red-400 text-sm border border-red-500/30 bg-red-500/[0.05] px-4 py-3 mb-8" data-testid="wallet-redeem-error">{redeemError}</p>}

            <h2 className="text-white text-2xl mb-4" style={H}>Redeem Points for Merch</h2>
            <div className="grid sm:grid-cols-3 gap-4 mb-12">
              {(wallet.catalog || []).map((item) => {
                const affordable = available >= Math.max(item.points_price, 1000);
                const pct = Math.min(100, Math.round((available / item.points_price) * 100));
                return (
                  <div key={item.catalog_item_id} className="border border-zinc-800 bg-zinc-900/40 p-5 flex flex-col" data-testid={`wallet-item-${item.catalog_item_id}`}>
                    <p className="text-white text-sm font-bold">{item.title}</p>
                    <p className="text-zinc-500 text-xs mt-1 mb-4">{item.points_price.toLocaleString()} pts · retail ${item.cash_price}</p>
                    {affordable ? (
                      <button onClick={() => redeem(item)} disabled={redeeming !== null}
                        className="mt-auto bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-wider py-2.5 disabled:opacity-40 hover:bg-[#d4ff5c] transition-colors"
                        data-testid={`wallet-redeem-${item.catalog_item_id}`}>
                        {redeeming === item.catalog_item_id ? 'Redeeming…' : 'Redeem'}
                      </button>
                    ) : (
                      <div className="mt-auto" data-testid={`wallet-progress-${item.catalog_item_id}`}>
                        <div className="h-1.5 bg-zinc-800 mb-1.5">
                          <div className="h-full bg-[#C7FA3E]/60" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-zinc-500 text-[11px]">{(item.points_price - available).toLocaleString()} points to go</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <h2 className="text-white text-2xl mb-4" style={H}>Recent Activity</h2>
            {(wallet.history || []).length === 0 ? (
              <p className="text-zinc-500 text-sm border border-zinc-800 bg-zinc-900/30 p-5" data-testid="wallet-history-empty">
                No points activity yet. Renewals, education modules, and webinars all earn points.
              </p>
            ) : (
              <div className="border border-zinc-800 divide-y divide-zinc-800" data-testid="wallet-history">
                {wallet.history.map((t, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 bg-zinc-900/30">
                    <div>
                      <p className="text-zinc-300 text-sm">{txnLabel(t)}</p>
                      <p className="text-zinc-600 text-[11px]">{String(t.created_at || '').slice(0, 10)} · {t.status}</p>
                    </div>
                    <p className={`text-sm font-bold ${t.points_delta >= 0 ? 'text-[#C7FA3E]' : 'text-red-400'}`}>
                      {t.points_delta >= 0 ? '+' : ''}{t.points_delta.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <p className="text-zinc-600 text-xs mt-8">
              Not a member yet? <Link to="/peptide-subscription" className="text-[#C7FA3E] hover:underline">Explore Biohacker Network memberships →</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
